// Per-store status for an image, derived from three pieces of Glance data:
//   - where the image data actually lives  -> image.locations[].metadata.store
//   - stores it is still copying to         -> os_glance_importing_to_stores
//   - stores a copy failed on               -> os_glance_failed_import
// The last two are comma-separated strings; a store lands in exactly one of the three states.
export type StoreState = 'stored' | 'importing' | 'failed'

export interface StoreStatus {
  id: string
  state: StoreState
}

// One entry of Glance's `locations` array. `metadata.store` is the store id the data lives in.
export interface ImageLocation {
  url?: string
  metadata?: { store?: string }
}

// Split a Glance comma-string into clean store ids (trimmed, empties dropped).
const splitStores = (value?: string): string[] =>
  (value ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)

// The stores an image is actually stored in, read from `locations`. This is the only source we
// have: the derived `stores` string is served by the Glance *admin* endpoint, but we call the
// *public* endpoint, which returns `locations` (each carrying metadata.store) instead.
const storedStores = (locations?: ImageLocation[]): string[] =>
  (locations ?? []).map((loc) => loc?.metadata?.store).filter((store): store is string => !!store)

interface StoreFields {
  locations?: ImageLocation[]
  os_glance_importing_to_stores?: string
  os_glance_failed_import?: string
}

// Build the per-store status list for one image. A store may appear in more than one property;
// status precedence is failed -> importing -> stored. Order is first-seen across
// stores, then importing, then failed.
export const deriveStoreStatuses = (image: StoreFields): StoreStatus[] => {
  const stored = storedStores(image?.locations)
  const importing = splitStores(image?.os_glance_importing_to_stores)
  const failed = splitStores(image?.os_glance_failed_import)
  const importingSet = new Set(importing)
  const failedSet = new Set(failed)

  const seen = new Set<string>()
  const order: string[] = []
  for (const id of [...stored, ...importing, ...failed]) {
    if (!seen.has(id)) {
      seen.add(id)
      order.push(id)
    }
  }

  return order.map((id) => ({
    id,
    state: failedSet.has(id) ? 'failed' : importingSet.has(id) ? 'importing' : 'stored',
  }))
}

interface FailureCandidate {
  id: string
  name: string
  storeStatuses?: StoreStatus[]
}

export interface FailureNotice {
  signature: string
  name: string
  failed: string[]
}

// Find images with failed store imports that have not been notified yet. Pure: does NOT mutate
// `seen` — the caller records signatures after toasting so a failure is announced only once.
export const collectNewFailures = (
  images: FailureCandidate[],
  seen: Set<string>,
): FailureNotice[] => {
  const notices: FailureNotice[] = []
  images.forEach((image) => {
    const failed = (image.storeStatuses ?? []).filter((s) => s.state === 'failed').map((s) => s.id)
    if (!failed.length) return
    const signature = `${image.id}:${[...failed].sort().join(',')}`
    if (seen.has(signature)) return
    notices.push({ signature, name: image.name, failed })
  })
  return notices
}

// True when an image should be offered "copy to all stores": it has a failed store, or a configured
// store it is neither stored-in nor currently importing-to. Fully-replicated and actively-importing
// images return false. With no configured stores to compare against, returns false. (Spec C3)
export const imageNeedsSync = (
  image: { storeStatuses?: StoreStatus[] },
  configuredStoreIds: string[],
): boolean => {
  if (!configuredStoreIds?.length) return false
  const statuses = image?.storeStatuses ?? []
  if (statuses.some((s) => s.state === 'failed')) return true
  const stateById = new Map(statuses.map((s) => [s.id, s.state]))
  return configuredStoreIds.some((id) => {
    const state = stateById.get(id)
    return state !== 'stored' && state !== 'importing'
  })
}

// The full set of stores in play across the image list. We seed it with whatever the store-info
// endpoint reports (which may be empty — that endpoint lives on the Glance admin API, and we call
// the public one) and union in every store any image is stored in, importing to, or failed on. That
// way a multi-store setup is still recognised from the image data alone. (Spec C2)
export const collectConfiguredStoreIds = (
  images: { storeStatuses?: StoreStatus[] }[],
  seedStoreIds: string[] = [],
): string[] => {
  const ids = new Set<string>(seedStoreIds)
  images.forEach((image) => (image?.storeStatuses ?? []).forEach((s) => ids.add(s.id)))
  return [...ids]
}

// Whether the "Copy to all stores" action should be enabled for the current selection: a multi-store
// site, a non-empty selection, and every selected image actually needing sync. (Spec C2)
export const canCopyToAllStores = (
  images: { storeStatuses?: StoreStatus[] }[],
  configuredStoreIds: string[],
): boolean =>
  configuredStoreIds.length > 1 &&
  images.length > 0 &&
  images.every((image) => imageNeedsSync(image, configuredStoreIds))
